import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import "@fontsource-variable/inter";
import "@/ui/styles.css";
import { installKeyboardLists } from "@/ui/kbnav";
import { initPlatform } from "@/platform";
import { pruneDrafts } from "@/app/drafts";
import { deviceState } from "@/modules/devices/device-state";
import { env } from "@/lib/env";
import { FatalBoundary, fatalHtml, flushPendingError, isStaleBuildError, reloadFresh, rescueUpdate, showDiagnosis } from "@/app/Fatal";

// 1.105.0: la marca de la recarga forzada (?_r=…) no queda en la dirección.
try { const u = new URL(location.href); if (u.searchParams.has("_r")) { u.searchParams.delete("_r"); history.replaceState(history.state, "", u.toString()); } } catch { /* nada */ }

// Una pantalla no pudo cargar un archivo. En la web suele ser una versión nueva publicada: recarga con la nueva.
// No se "silencia" el error (con preventDefault la pantalla quedaba en "reading 'default'" sin el motivo).
window.addEventListener("vite:preloadError", (e) => {
  const err = (e as Event & { payload?: unknown }).payload;
  (globalThis as { __sofiaPreloadError?: string }).__sofiaPreloadError = String((err as Error)?.message ?? err ?? "");
  console.error("No se pudo cargar un archivo de la app:", err);
  if (env.platform === "web") void reloadFresh();
});

// Entorno de pruebas: franja fija para no confundirlo con el sistema real.
if (env.entorno === "pruebas") {
  const r = document.createElement("div");
  r.textContent = `ENTORNO DE PRUEBAS · ${env.version} · los datos no son reales`;
  r.setAttribute("style", "position:fixed;z-index:99999;left:0;right:0;top:0;height:18px;line-height:18px;text-align:center;font:700 11px system-ui,sans-serif;letter-spacing:.04em;color:#3b2500;background:repeating-linear-gradient(45deg,#ffcc4d,#ffcc4d 12px,#ffb300 12px,#ffb300 24px);pointer-events:none;opacity:.92");
  document.body.appendChild(r);
  document.title = `[PRUEBAS] ${document.title}`;
}

async function boot() {
  // Arranque en frío: plataforma y estado del equipo antes de montar (funciona offline).
  await initPlatform();
  pruneDrafts();
  await deviceState.load();
  if (env.sentryDsn) {
    Sentry.init({ dsn: env.sentryDsn, release: `sofia@${env.version}`, environment: env.platform, tracesSampleRate: 0.05 });
    const d = deviceState.get();
    if (d) Sentry.setTags({ device: d.prefix, business: d.businessSlug });
  }
  setTimeout(flushPendingError, 5000);
  const { default: App } = await import("@/app/App");
  installKeyboardLists();
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <FatalBoundary version={env.version}>
        <App />
      </FatalBoundary>
    </StrictMode>,
  );
}

// Si el arranque falla (por ejemplo, justo después de una actualización), se muestra el motivo en vez de una pantalla negra.
boot().catch(async (e: unknown) => {
  const err = e as Error;
  console.error("No se pudo iniciar la app:", err);
  if (isStaleBuildError(err) && (await reloadFresh())) return;
  const root = document.getElementById("root");
  if (root) root.innerHTML = fatalHtml(`Versión ${env.version}\n${err?.message ?? String(e)}\n${(err?.stack ?? "").split("\n").slice(0, 6).join("\n")}`);
  void rescueUpdate();
  void showDiagnosis(err?.message ?? String(e));
});
